import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CMP-03, CMP-05, CMP-07 and VEN-05 at v1.965, compliance part 1: ticket types; what a
// venue, a role and a job require; what each crew member holds; and where a gap is refused.

export type TicketType = components['schemas']['TicketTypeView'];
export type CrewTicket = components['schemas']['CrewTicketView'];
export type CrewInduction = components['schemas']['CrewInductionView'];
export type Enforcement = components['schemas']['EnforcementView']['complianceEnforcement'];
export type Gap = components['schemas']['ComplianceGapView'];

export const ENFORCEMENT: Record<Enforcement, string> = {
  warn: 'Warn only — never refuse',
  accept: 'Refuse at acceptance (offering is fine)',
  offer: 'Refuse at offer',
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

/** "White Card, Crown induction": what someone lacks, in a line. */
export function gapsLabel(gaps: readonly Gap[]): string {
  return gaps.map((g) => g.name).join(', ');
}

export const ticketTypesQuery = queryOptions({
  queryKey: ['ticket-types'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/ticket-types');
    if (error) throw problem(error, 'Could not load the tickets.');
    return data;
  },
});

export async function addTicketType(name: string) {
  const { data, error } = await api.POST('/v1/ticket-types', { body: { name } });
  if (error) throw problem(error, 'Could not add that ticket.');
  return data;
}

type Owner = 'venues' | 'crew-roles' | 'jobs';

export const requiredQuery = (owner: Owner, id: string) =>
  queryOptions({
    queryKey: ['required-tickets', owner, id],
    queryFn: async () => {
      const path =
        owner === 'venues'
          ? api.GET('/v1/venues/{id}/required-tickets', { params: { path: { id } } })
          : owner === 'crew-roles'
            ? api.GET('/v1/crew-roles/{id}/required-tickets', { params: { path: { id } } })
            : api.GET('/v1/jobs/{id}/required-tickets', { params: { path: { id } } });
      const { data, error } = await path;
      if (error) throw problem(error, 'Could not load the required tickets.');
      return data;
    },
  });

export async function setRequired(owner: Owner, id: string, ticketTypeIds: string[]) {
  const body = { ticketTypeIds };
  const call =
    owner === 'venues'
      ? api.PUT('/v1/venues/{id}/required-tickets', { params: { path: { id } }, body })
      : owner === 'crew-roles'
        ? api.PUT('/v1/crew-roles/{id}/required-tickets', { params: { path: { id } }, body })
        : api.PUT('/v1/jobs/{id}/required-tickets', { params: { path: { id } }, body });
  const { data, error } = await call;
  if (error) throw problem(error, 'Could not save the required tickets.');
  return data;
}

export const crewTicketsQuery = (crewMemberId: string) =>
  queryOptions({
    queryKey: ['crew', crewMemberId, 'tickets'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/crew/{id}/tickets', {
        params: { path: { id: crewMemberId } },
      });
      if (error) throw problem(error, 'Could not load their tickets.');
      return data;
    },
  });

export async function addCrewTicket(
  crewMemberId: string,
  body: {
    ticketTypeId: string;
    number?: string | null;
    issuer?: string | null;
    issuedOn?: string | null;
    expiresOn?: string | null;
    fileId?: string | null;
  },
) {
  const { data, error } = await api.POST('/v1/crew/{id}/tickets', {
    params: { path: { id: crewMemberId } },
    body,
  });
  if (error) throw problem(error, 'Could not add that ticket.');
  return data;
}

export async function retireCrewTicket(id: string) {
  const { error } = await api.POST('/v1/crew-tickets/{id}/retire', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not remove that ticket.');
}

export const crewInductionsQuery = (crewMemberId: string) =>
  queryOptions({
    queryKey: ['crew', crewMemberId, 'inductions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/crew/{id}/inductions', {
        params: { path: { id: crewMemberId } },
      });
      if (error) throw problem(error, 'Could not load their inductions.');
      return data;
    },
  });

export async function addCrewInduction(
  crewMemberId: string,
  body: { venueId: string; doneOn: string; fileId?: string | null },
) {
  const { data, error } = await api.POST('/v1/crew/{id}/inductions', {
    params: { path: { id: crewMemberId } },
    body,
  });
  if (error) throw problem(error, 'Could not record that induction.');
  return data;
}

export async function retireCrewInduction(id: string) {
  const { error } = await api.POST('/v1/crew-inductions/{id}/retire', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not remove that induction.');
}

/** A photo or scan of the card, kept on the crew member; returns the file's id. */
export async function uploadCrewCard(crewMemberId: string, file: File): Promise<string> {
  const data = new FormData();
  data.append('file', file);
  const { data: kept, error } = await api.POST('/v1/crew/{id}/files', {
    params: { path: { id: crewMemberId } },
    body: data as never,
    bodySerializer: (body) => body as unknown as FormData,
  });
  if (error) throw problem(error, 'Could not upload that photo.');
  return kept.id;
}

export const enforcementQuery = queryOptions({
  queryKey: ['compliance-enforcement'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/crew/compliance-enforcement');
    if (error) throw problem(error, 'Could not load the setting.');
    return data.complianceEnforcement;
  },
});

export async function setEnforcement(complianceEnforcement: Enforcement) {
  const { data, error } = await api.PUT('/v1/crew/compliance-enforcement', {
    body: { complianceEnforcement },
  });
  if (error) throw problem(error, 'Could not save the setting.');
  return data.complianceEnforcement;
}
